import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './index.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { showToast } from '../../../components/ToastMsg';
import BeatLoader from 'react-spinners/BeatLoader';
import Dropdown from '../../../components/Dropdown/Dropdown';
import { selectActiveCertificateTypesList, STATUS } from '../../../redux/features/certificateType/certificateTypeSlice';
import { resetCertificateTemplateDetails, selectCertificateTemplateList } from '../../../redux/features/certificateTemplate/certificateTemplateSlice';
import { createCertificateTemplate, getAllCertificateTemplates, updateCertificateTemplate } from '../../../redux/features/certificateTemplate/certificateTemplateService';
import { getActiveCertificateTypes } from '../../../redux/features/certificateType/certificateTypeService';
import SearchBar from '../../../components/SearchBar';
import TableSkeleton from '../../../components/Skeleton';
import NoResultsFound from '../../../components/NoResultsFound';
import Pagination from '../../../components/Pagination';
import { Link } from 'react-router-dom';
import Modal from '../../../components/Modal';
import { faCircleCheck, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { selectActiveCertificateList } from '../../../redux/features/certificateEntity/certificateEntitySlice';
import { getActiveCertificateEntities } from '../../../redux/features/certificateEntity/certificateEntityService';
import { resetCertificateTemplateVersionDetails } from '../../../redux/features/certificateTemplateVersion/certificateTemplateVersionSlice';

const CertificateTemplate = () => {
  const dispatch = useDispatch();
  const { status, data: certificateTypes } = useSelector(selectActiveCertificateTypesList);
  const isTypesLoading = status === STATUS.LOADING;

  const { status: templatesStatus, data: certificateTemplates, pageCount } = useSelector(selectCertificateTemplateList);
  const isTemplatesLoading = templatesStatus === STATUS.LOADING;
  const { status: entityStatus, data: activeCertificateEntities } = useSelector(selectActiveCertificateList);

  const dropdownRef = useRef(null);
  const entityRef = useRef(null);
  const templateRef = useRef(null);
  const headerLogoRef = useRef(null);
  const footerLogoRef = useRef(null);
  const backgroundImageRef = useRef(null);

  const [formData, setFormData] = useState({
    certificate_type_id: '',
    entity_id: '',
    name: '',
    template: null,
    header_logo: null,
    footor_logo: null,
    background_image: null,
    is_version_available: false,
  });

  const [errorMessages, setErrorMessages] = useState({
    certificate_type_id: '',
    entity_id: '',
    name: '',
    template: '',
    header_logo: '',
    footor_logo: '',
    background_image: '',
  });

  const [disabledBtn, setBtnDisable] = useState(false);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState({
    id: '',
    name: ''
  });
  const [selectedEntity, setSelectedEntity] = useState({
    id: '',
    name: ''
  });

  const fetchAllTemplates = useCallback(() => {
    dispatch(getAllCertificateTemplates({
      skip,
      limit,
      search: searchQuery,
    }));
  }, [dispatch, skip, limit, searchQuery]);

  useEffect(() => {
    fetchAllTemplates();
    dispatch(getActiveCertificateTypes());
    dispatch(getActiveCertificateEntities());
  }, [fetchAllTemplates]);

  const handleType = (option) => {
    setFormData({ ...formData, certificate_type_id: option.id });
    setErrorMessages({ ...errorMessages, certificate_type_id: '' });
    setSelectedType({
      id: option.id,
      name: option.name,
    })
  };

  const handleEntity = (option) => {
    setFormData({ ...formData, entity_id: option.id });
    setErrorMessages({ ...errorMessages, entity_id: '' });
    setSelectedEntity({
      id: option.id,
      name: option.name,
    })
  };

  const validateFile = (selectedFile, fieldName) => {
    const allowedTypes = ['image/jpeg', 'image/png'];

    if (!allowedTypes.includes(selectedFile.type)) {
      setErrorMessages({
        ...errorMessages,
        [fieldName]: 'Invalid file type. Allowed types: JPEG, PNG',
      });
      setFormData({ ...formData, [fieldName]: null });
      return false;
    }

    setErrorMessages({ ...errorMessages, [fieldName]: null });
    setFormData({ ...formData, [fieldName]: selectedFile });
    return true;
  };

  const handleDrop = (event, fieldName) => {
    event.preventDefault();
    event.stopPropagation();

    const selectedFile = event.dataTransfer.files[0];

    if (selectedFile) {
      validateFile(selectedFile, fieldName);
    }
  };

  const handleFileChange = (event, fieldName) => {
    if (event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      validateFile(selectedFile, fieldName);
    }
  };

  const handleClick = (ref) => {
    ref.current.click();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrorMessages((prev) => ({
      ...prev,
      [name]: '',
    }));
  };

  // Validate input
  const isValidTemplate = () => {
    const errors = {};
    const trimmedName = formData.name.trim();

    if (!trimmedName) {
      errors.name = 'Name is required.';
    }
    if (!formData.certificate_type_id) {
      errors.certificate_type_id = 'Certificate Type is required.';
    }
    if (!formData.entity_id) {
      errors.entity_id = 'Entity is required.';
    }
    if (!formData.template) errors.template = 'Template image is required.';
    if (!formData.header_logo) errors.header_logo = 'Header logo is required.';
    if (!formData.footor_logo) errors.footor_logo = 'Footer logo is required.';
    if (!formData.background_image) errors.background_image = 'Background image is required.';

    if (Object.keys(errors).length > 0) {
      setErrorMessages(errors);
      return false;
    } else {
      return true;
    }
  };

  const insertNewTemplate = async () => {
    if (isValidTemplate(formData)) {
      setBtnDisable(true);
      try {
        const payload = {
          ...formData,
          is_version_available: formData.is_version_available ? "True" : "False",
        };
        const response = await dispatch(createCertificateTemplate(payload)).unwrap();
        if (response.status === true) {
          showToast('success', 'Certificate template created successfully.');
        }
        setBtnDisable(false);
        setFormData({
          certificate_type_id: '',
          entity_id: '',
          name: '',
          template: null,
          header_logo: null,
          footor_logo: null,
          background_image: null,
        });
        dropdownRef.current?.clearSelection();
        entityRef.current?.clearSelection();
        templateRef.current.value = '';
        headerLogoRef.current.value = '';
        footerLogoRef.current.value = '';
        backgroundImageRef.current.value = '';
        setSelectedType({ id: '', name: '' });
        setSelectedEntity({ id: '', name: '' });
        fetchAllTemplates();
      } catch (error) {
        setBtnDisable(false);
        showToast('error', error.message);
      }
    }
  };

  const handlePageChange = (pageNumber) => {
    setSkip(pageNumber - 1);
  };

  // Status update
  const [isActivateModalOpen, setActivateModalOpen] = useState(false);
  const [isDeactivationModalOpen, setDeactivationModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const closeModal = () => setActivateModalOpen(false);
  const closDeactivationModal = () => setDeactivationModalOpen(false);

  const handleYes = async () => {
    setActivateModalOpen(false);
    try {
      const response = await dispatch(updateCertificateTemplate({ id: selectedTemplate, status: true })).unwrap();
      setSelectedTemplate(null);
      showToast('success', response.message);
      fetchAllTemplates();
    }
    catch (error) {
      showToast('error', error.message);
    };
  }

  const handleRejectYes = async () => {
    setDeactivationModalOpen(false);
    try {
      const response = await dispatch(updateCertificateTemplate({ id: selectedTemplate, status: false })).unwrap();
      setSelectedTemplate(null);
      showToast('success', response.message);
      fetchAllTemplates();
    }
    catch (error) {
      showToast('error', error.message);
    };
  }

  return (
    <div className={`card-section`}>
      <div className={`main-card`}>
        <h1 className={`form-title`}>New Certificate Template</h1>
        <div className={styles.formContainer}>
          <form className={styles.coordinatorForm} noValidate>
            <div className={`form-input-row`}>
              <div className={`form-input-col md-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor='full_name' className={`form-label`}>
                    Entity<span className={`required-star`}>*</span>
                  </label>
                  <Dropdown
                    ref={entityRef}
                    options={activeCertificateEntities}
                    placeholder="Choose an Option"
                    onSelect={handleEntity}
                    value={selectedEntity.name}
                  />
                  {errorMessages.entity_id && (
                    <p className={`text-danger`}>{errorMessages.entity_id}</p>
                  )}
                </div>
              </div>
            </div>
            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor='lottery-brand' className={`form-label`}>
                    Certificate Type<span className={`required-star`}>*</span>
                  </label>
                  <Dropdown
                    ref={dropdownRef}
                    options={certificateTypes}
                    placeholder='Select a Type'
                    onSelect={handleType}
                    value={selectedType.name}
                  />
                  {errorMessages.certificate_type_id && (
                    <p className={`text-danger`}>
                      {errorMessages.certificate_type_id}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor='full_name' className={`form-label`}>
                    Name<span className={`required-star`}>*</span>
                  </label>
                  <input
                    type='text'
                    id='name'
                    name='name'
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder='Enter Name'
                    className={`form-input-default`}
                  />
                  {errorMessages.name && (
                    <p className={`text-danger`}>{errorMessages.name}</p>
                  )}
                </div>
              </div>
            </div>
            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor='full_name' className={`form-label`}>
                    Sample Image
                    <span className={`required-star`}>*</span>
                  </label>
                  <div
                    onDrop={(e) => handleDrop(e, 'template')}
                    onDragOver={(e) => e.preventDefault()}
                    onClick={() => handleClick(templateRef)}
                    className={styles.dropInput}
                  >
                    <input
                      type="file"
                      ref={templateRef}
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'template')}
                      style={{ display: 'none' }}
                    />
                    {formData.template ? (
                      <img src={URL.createObjectURL(formData.template)} className={styles.thumbnailImg} />
                    ) : (
                      <p>Drag & Drop or Click</p>
                    )}
                  </div>
                  {errorMessages.template && <p className="text-danger">{errorMessages.template}</p>}
                </div>
              </div>
            </div>
            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor='full_name' className={`form-label`}>
                    Header Logo
                    <span className={`required-star`}>*</span>
                  </label>
                  <div
                    onDrop={(e) => handleDrop(e, 'header_logo')}
                    onDragOver={(e) => e.preventDefault()}
                    onClick={() => handleClick(headerLogoRef)}
                    className={styles.dropInput}
                  >
                    <input
                      type="file"
                      ref={headerLogoRef}
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'header_logo')}
                      style={{ display: 'none' }}
                    />
                    {formData.header_logo ? (
                      <img src={URL.createObjectURL(formData.header_logo)} className={styles.thumbnailImg} />
                    ) : (
                      <p>Drag & Drop or Click</p>
                    )}
                  </div>
                  {errorMessages.header_logo && <p className="text-danger">{errorMessages.header_logo}</p>}
                </div>
              </div>
            </div>
            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor='full_name' className={`form-label`}>
                    Footer Logo
                    <span className={`required-star`}>*</span>
                  </label>
                  <div
                    onDrop={(e) => handleDrop(e, 'footor_logo')}
                    onDragOver={(e) => e.preventDefault()}
                    onClick={() => handleClick(footerLogoRef)}
                    className={styles.dropInput}
                  >
                    <input
                      type="file"
                      ref={footerLogoRef}
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'footor_logo')}
                      style={{ display: 'none' }}
                    />
                    {formData.footor_logo ? (
                      <img src={URL.createObjectURL(formData.footor_logo)} className={styles.thumbnailImg} />
                    ) : (
                      <p>Drag & Drop or Click</p>
                    )}
                  </div>
                  {errorMessages.footor_logo && <p className="text-danger">{errorMessages.footor_logo}</p>}
                </div>
              </div>
            </div>
            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <div className={`form-input-group`}>
                  <label htmlFor='full_name' className={`form-label`}>
                    Background Image
                    <span className={`required-star`}>*</span>
                  </label>
                  <div
                    onDrop={(e) => handleDrop(e, 'background_image')}
                    onDragOver={(e) => e.preventDefault()}
                    onClick={() => handleClick(backgroundImageRef)}
                    className={styles.dropInput}
                  >
                    <input
                      type="file"
                      ref={backgroundImageRef}
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'background_image')}
                      style={{ display: 'none' }}
                    />
                    {formData.background_image ? (
                      <img src={URL.createObjectURL(formData.background_image)} className={styles.thumbnailImg} />
                    ) : (
                      <p>Drag & Drop or Click</p>
                    )}
                  </div>
                  {errorMessages.background_image && <p className="text-danger">{errorMessages.background_image}</p>}
                </div>
              </div>
            </div>
            <div className={`form-input-row`}>
              <div className={`form-input-col`}>
                <label className={`form-label`}>
                  Is Version Available?
                </label>
                <input
                  type="checkbox"
                  checked={formData.is_version_available}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_version_available: e.target.checked,
                    }))
                  }
                />
              </div>
            </div>
            <button
              className={`btn-primary`}
              type='button'
              disabled={disabledBtn}
              onClick={insertNewTemplate}
            >
              {disabledBtn ? (
                <BeatLoader grade='#ffffff' />
              ) : (
                `Create`
              )}
            </button>
          </form>
        </div>
      </div>
      <div className={`search-card`}>
        <div className={`search-card-header`}>
          <h1 className={`form-title`}>Certificate Templates</h1>
          <SearchBar onSearch={(query) => setSearchQuery(query)} />
        </div>
        <div className={`search-card-body`}>
          {isTemplatesLoading ? (
            <TableSkeleton
              columns={["Name", "Type", "Template", "Status", "Actions"]}
              rowCount={10}
              height={20}
            />
          ) : certificateTemplates.length > 0 ? (
            <div className={`table-container`}>
              <table className={`table`}>
                <thead className={`thead`}>
                  <tr>
                    <th className={`theadColumnOne`}>Name</th>
                    <th>Type</th>
                    <th>Template</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody className={`tbody`}>
                  {certificateTemplates.map((template) => (
                    <tr key={template.id}>
                      <td className={`tdataColumnOne`}>{template.name}</td>
                      <td>{template?.certificate_type?.name}</td>
                      <td><img src={template?.template_url} alt='Template' className={styles.templateImg} /></td>
                      <td>{template?.status ? 'Active' : 'Inactive'}</td>
                      <td className={`actionsBtn`}>
                        <Link
                          to={`/admin/certificate-template/${template.id}`}
                          className={`link-edit-label`}
                          onClick={() => {
                            // optional: reset selected template before navigation
                            dispatch(resetCertificateTemplateDetails());
                          }}
                        >
                          Edit
                        </Link>
                        {template?.is_version_available &&  <Link
                          to={`/admin/certificate-template/${template.id}/versions`}
                          className={`link-edit-label`}
                          onClick={() => {
                            // optional: reset selected template before navigation
                            dispatch(resetCertificateTemplateVersionDetails());
                          }}
                        >
                          Versions
                        </Link>}
                        <button
                          className={template.status ? `btn-inactive` : `btn-active`}
                          onClick={() => {
                            setSelectedTemplate(template.id);
                            template.status ? setDeactivationModalOpen(true) : setActivateModalOpen(true);
                          }
                          }>{template.status ? 'Deactivate' : 'Acitvate'}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <NoResultsFound />
          )}
        </div>
        <div className={`search-card-footer`}>
          {pageCount > 0 && (
            <div className={styles.paginationContainer}>
              <Pagination
                currentPage={skip + 1}
                totalPages={pageCount}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
      {isActivateModalOpen && (
        <Modal
          title='Certificate Template Activation'
          onClose={closeModal}
          onYes={handleYes}
          isInput={false}
          iconName={faCircleCheck}
          bodyText={`Are you sure you want to activate the certificate template?`}
          colorType={`var(--green)`}
        />
      )}
      {isDeactivationModalOpen && (
        <Modal
          title='Certificate Template Deactivation'
          onClose={closDeactivationModal}
          onYes={handleRejectYes}
          isInput={false}
          iconName={faCircleXmark}
          bodyText={`Are you sure you want to deactivate the certificate template?`}
          colorType={`var(--danger)`}
        />
      )}
    </div>
  );
};

export default CertificateTemplate;
